// src/pages/SalaryManagement.js

import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Print as PrintIcon
} from '@mui/icons-material';

const API_URL = 'https://htm.bayex.lk/api/salary_management.php';
const SMS_API = 'https://htm.bayex.lk/api/send_salary_sms.php';

const fmt     = n => Number(n || 0).toFixed(2);

export default function SalaryManagement() {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const today    = new Date();

  // year/month filters
  const [year, setYear]   = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);

  // fetched salary slips array
  const [salarySlips, setSalarySlips] = useState([]);
  const [loading,     setLoading]     = useState(false);

  // “settle” in‐flight indicator
  const [settlingId, setSettlingId] = useState(null);

  // slip dialog state
  const [openSlip, setOpenSlip] = useState(false);
  const [slip,     setSlip]     = useState(null);

  // Inject print styles (80 mm) once
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @media print {
        @page { size: 80mm auto; margin: 0; }
        body * { visibility: hidden; }
        .print-container, .print-container * { visibility: visible; }
        .print-container { position: absolute; top: 0; left: 0; width: 80mm; }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Fetch salary slips for the given year/month
  const fetchData = () => {
    setLoading(true);
    fetch(`${API_URL}?year=${year}&month=${month}`)
      .then(r => r.json())
      .then((data) => {
        // Expect an array of slip objects { staff_id, staff_name, days_present, basic_earning, extra_earning,
        //   bonus_total, penalty_total, loan_deduction, advance_deduction, total_salary,
        //   daily_breakdown: [...], 
        //   advance_payments: [ { date: 'YYYY-MM-DD', amount: 500.00 }, ... ]
        // }
        setSalarySlips(Array.isArray(data) ? data : []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(fetchData, [year, month]);

  // Mark a salary as “settled”
  const settleSalary = (s) => {
    if (s.settled) return;
    if (!window.confirm(`Mark ${s.staff_name} as settled?`)) return;
    setSettlingId(s.staff_id);

    fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'settle', year, month, staff_id: s.staff_id })
    })
      .then(r => r.json())
      .then(d => {
        setSettlingId(null);
        if (d.status === 'success') {
          fetchData();
        } else {
          alert('Error: ' + d.message);
        }
      })
      .catch(err => {
        console.error(err);
        setSettlingId(null);
        alert('Network error');
      });
  };

  const sendSalarySMS = slip => {
  fetch(SMS_API, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      to:           slip.staff_phone,
      staff_id:     slip.staff_id,
      staff_name:   slip.staff_name,
      total_salary: slip.total_salary,
      year,
      month
    })
  })
  .then(r => r.json())
  .then(d => {
    if (d.status === 'sent') alert(`SMS sent to ${slip.staff_name}`);
    else                  alert('SMS error: ' + d.sms_response);
  })
  .catch(() => alert('Network error'));
};


  const showSlip  = (s) => { setSlip(s); setOpenSlip(true); };
  const closeSlip = () => { setSlip(null); setOpenSlip(false); };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Salary Management
      </Typography>

      {/* ───────── Filters ───────── */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Year"
          size="small"
          type="number"
          value={year}
          onChange={e => setYear(+e.target.value)}
        />
        <TextField
          label="Month"
          size="small"
          type="number"
          inputProps={{ min: 1, max: 12 }}
          value={month}
          onChange={e => setMonth(+e.target.value)}
        />
        <Button startIcon={<RefreshIcon />} variant="contained" onClick={fetchData}>
          Refresh
        </Button>
      </Box>

      {/* ───────── Salary List ───────── */}
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : salarySlips.length === 0 ? (
        <Typography>No data for {month}/{year}.</Typography>
      ) : isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {salarySlips.map(s => (
            <Card key={s.staff_id} variant="outlined">
              <CardContent>
                <Typography variant="h6">{s.staff_name}</Typography>
                <Typography><b>Days</b>: {s.days_present}</Typography>
                <Typography><b>Basic</b>: Rs {fmt(s.basic_earning)}</Typography>
                <Typography><b>Extras</b>: Rs {fmt(s.extra_earning)}</Typography>
                <Typography sx={{ color: 'green' }}>
                  <b>Bonus</b>: +Rs {fmt(s.bonus_total)}
                </Typography>
                <Typography sx={{ color: 'red' }}>
                  <b>Penalties</b>: −Rs {fmt(s.penalty_total)}
                </Typography>
                <Typography><b>Loan</b>: −Rs {fmt(s.loan_deduction)}</Typography>
                <Typography><b>Advance</b>: −Rs {fmt(s.advance_deduction)}</Typography>
                <Typography><b>Net</b>: Rs {fmt(s.total_salary)}</Typography>
              </CardContent>
              <CardActions>
                <Button size="small" variant="outlined" onClick={() => showSlip(s)}>
                  View Slip
                </Button>
                <Button
  size="small"
  variant="outlined"
  onClick={() => sendSalarySMS(s)}
>
  SMS Salary Sheet
</Button>

                <Button
                  size="small"
                  variant="contained"
                  disabled={s.settled || settlingId === s.staff_id}
                  onClick={() => settleSalary(s)}
                >
                  {settlingId === s.staff_id
                    ? 'Settling…'
                    : s.settled
                      ? 'Settled'
                      : 'Settle'
                  }
                </Button>
              </CardActions>
            </Card>
          ))}
        </Box>
      ) : (
        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Staff</TableCell>
                <TableCell align="right">Days</TableCell>
                <TableCell align="right">Basic</TableCell>
                <TableCell align="right">Extras</TableCell>
                <TableCell align="right">Bonus</TableCell>
                <TableCell align="right">Penalties</TableCell>
                <TableCell align="right">Loan</TableCell>
                <TableCell align="right">Advance</TableCell>
                <TableCell align="right">Net</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {salarySlips.map(s => (
                <TableRow key={s.staff_id}>
                  <TableCell>{s.staff_name}</TableCell>
                  <TableCell align="right">{s.days_present}</TableCell>
                  <TableCell align="right">{fmt(s.basic_earning)}</TableCell>
                  <TableCell align="right">{fmt(s.extra_earning)}</TableCell>
                  <TableCell align="right" sx={{ color: 'green' }}>
                    {fmt(s.bonus_total)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'red' }}>
                    {fmt(s.penalty_total)}
                  </TableCell>
                  <TableCell align="right">{fmt(s.loan_deduction)}</TableCell>
                  <TableCell align="right">{fmt(s.advance_deduction)}</TableCell>
                  <TableCell align="right">{fmt(s.total_salary)}</TableCell>
                  <TableCell align="center">
                    <Button size="small" variant="outlined" onClick={() => showSlip(s)}>
                      View Slip
                    </Button>
                    <Button
  size="small"
  variant="outlined"
  sx={{ mx: 1 }}
  onClick={() => sendSalarySMS(s)}
>
  SMS Salary Sheet
</Button>

                    <Button
                      size="small"
                      variant="contained"
                      sx={{ ml: 1 }}
                      disabled={s.settled || settlingId === s.staff_id}
                      onClick={() => settleSalary(s)}
                    >
                      {settlingId === s.staff_id
                        ? 'Settling…'
                        : s.settled
                          ? 'Settled'
                          : 'Settle'
                      }
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* ───────── Slip Dialog ───────── */}
      <Dialog open={openSlip} onClose={closeSlip} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Salary Slip
            <IconButton onClick={() => window.print()} size="small">
              <PrintIcon fontSize="inherit" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {slip && (
            <Box className="print-container" sx={{ p: 1 }}>
              {/* Header */}
              <Typography variant="h6" align="center">
                {slip.staff_name}
              </Typography>
              <Typography align="center" color="text.secondary" gutterBottom>
                Period: {month.toString().padStart(2, '0')}/{year}
              </Typography>

              {/* Summary Table */}
              <Paper variant="outlined" sx={{ my: 2, p: 2 }}>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>Days Present</TableCell>
                      <TableCell align="right">{slip.days_present}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Basic Earned</TableCell>
                      <TableCell align="right">Rs {fmt(slip.basic_earning)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Tips Share</TableCell>
                      <TableCell align="right">
                        Rs {fmt(slip.daily_breakdown.reduce((sum, d) => sum + d.daily_tip, 0))}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Svc‐Chg Share</TableCell>
                      <TableCell align="right">
                        Rs {fmt(slip.daily_breakdown.reduce((sum, d) => sum + d.daily_svc, 0))}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ color: 'green' }}>Bonus</TableCell>
                      <TableCell align="right" sx={{ color: 'green' }}>
                        + Rs {fmt(slip.bonus_total)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ color: 'red' }}>Penalties</TableCell>
                      <TableCell align="right" sx={{ color: 'red' }}>
                        − Rs {fmt(slip.penalty_total)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Loan Deduction</TableCell>
                      <TableCell align="right">− Rs {fmt(slip.loan_deduction)}</TableCell>
                    </TableRow>

                    {/* ─── Advance Deduction (total) ─── */}
                    <TableRow>
                      <TableCell>Advance Deduction</TableCell>
                      <TableCell align="right">− Rs {fmt(slip.advance_deduction)}</TableCell>
                    </TableRow>

                    {/* ─── Advance History (each advance) ─── */}
                    {Array.isArray(slip.advance_payments) && slip.advance_payments.length > 0 && (
                      <TableRow>
                        <TableCell>Advance History</TableCell>
                        <TableCell align="right">
                          {slip.advance_payments
                            .map((a, idx) => `${a.date}: Rs ${fmt(a.amount)}`)
                            .join(', ')}
                        </TableCell>
                      </TableRow>
                    )}

                    {/* ─── Net Pay ─── */}
                    <TableRow>
                      <TableCell><b>Net Pay</b></TableCell>
                      <TableCell align="right"><b>Rs {fmt(slip.total_salary)}</b></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Paper>

              {/* Daily Breakdown */}
              <Typography variant="subtitle1" gutterBottom>
                Daily Breakdown
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Basic</TableCell>
                    <TableCell align="right">Tips</TableCell>
                    <TableCell align="right">Svc (50%)</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {slip.daily_breakdown.map((d, i) => (
                    <TableRow key={i}>
                      <TableCell>{d.date}</TableCell>
                      <TableCell align="right">{fmt(d.daily_basic)}</TableCell>
                      <TableCell align="right">{fmt(d.daily_tip)}</TableCell>
                      <TableCell align="right">{fmt(d.daily_svc)}</TableCell>
                      <TableCell align="right">{fmt(d.daily_total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSlip}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
